/**
 * Analytics Service
 * Tracks user sessions, page views, and app activity
 * 
 * NOTE: Requires migration 039_analytics_tracking.sql to be run.
 * If tables don't exist, analytics will fail silently.
 */

import { supabase } from '@/lib/supabase';
import { supabaseUrl, supabaseAnonKey } from '@/lib/supabaseRest';
// A public route's token (`/sign/:token`, `/documents/:token`) never reaches
// an analytics row: every stored path goes through this (see the module).
import { redactPublicTokenPath } from '@/lib/publicTokenPath';

// Flag to track if analytics tables exist (set to false on first failure)
let analyticsEnabled = true;

// Types
export interface SessionInfo {
  id: string;
  userId: string;
  sessionStart: Date;
  isActive: boolean;
}

export interface DeviceInfo {
  userAgent: string;
  browserName: string;
  browserVersion: string;
  osName: string;
  osVersion: string;
  deviceType: 'desktop' | 'mobile' | 'tablet';
  screenWidth: number;
  screenHeight: number;
}

export interface LocationInfo {
  ipAddress?: string;
  city?: string;
  region?: string;
  country?: string;
  countryCode?: string;
  timezone?: string;
  latitude?: number;
  longitude?: number;
}

export interface PageViewEvent {
  pagePath: string;
  pageTitle?: string;
  pageCategory?: string;
  referrerPath?: string;
  queryParams?: Record<string, string>;
}

export interface ActivityLogEvent {
  activityType: string;
  activityCategory?: string;
  description?: string;
  entityType?: string;
  entityId?: string;
  details?: Record<string, unknown>;
}

export interface ErrorEvent {
  errorType: 'javascript' | 'api' | 'validation' | 'render';
  errorMessage: string;
  errorStack?: string;
  pagePath?: string;
  componentName?: string;
  actionAttempted?: string;
  requestUrl?: string;
  requestMethod?: string;
  responseStatus?: number;
}

// Current session storage
let currentSession: SessionInfo | null = null;
let currentPageViewId: string | null = null;
let pageEntryTime: Date | null = null;

/**
 * Parse user agent to extract device info
 */
function parseUserAgent(): DeviceInfo {
  const ua = navigator.userAgent;
  
  // Detect browser
  let browserName = 'Unknown';
  let browserVersion = '';
  
  if (ua.includes('Firefox/')) {
    browserName = 'Firefox';
    browserVersion = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Edg/')) {
    browserName = 'Edge';
    browserVersion = ua.match(/Edg\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Chrome/')) {
    browserName = 'Chrome';
    browserVersion = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browserName = 'Safari';
    browserVersion = ua.match(/Version\/(\d+\.\d+)/)?.[1] || '';
  }
  
  // Detect OS
  let osName = 'Unknown';
  let osVersion = '';
  
  if (ua.includes('Windows NT')) {
    osName = 'Windows';
    const ntVersion = ua.match(/Windows NT (\d+\.\d+)/)?.[1];
    if (ntVersion === '10.0') osVersion = '10/11';
    else if (ntVersion === '6.3') osVersion = '8.1';
    else if (ntVersion === '6.2') osVersion = '8';
    else if (ntVersion === '6.1') osVersion = '7';
  } else if (ua.includes('Mac OS X')) {
    osName = 'macOS';
    osVersion = ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace('_', '.') || '';
  } else if (ua.includes('Linux')) {
    osName = 'Linux';
  } else if (ua.includes('Android')) {
    osName = 'Android';
    osVersion = ua.match(/Android (\d+\.\d+)/)?.[1] || '';
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    osName = 'iOS';
    osVersion = ua.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || '';
  }
  
  // Detect device type
  let deviceType: 'desktop' | 'mobile' | 'tablet' = 'desktop';
  if (/Mobi|Android/i.test(ua) && !/iPad/i.test(ua)) {
    deviceType = 'mobile';
  } else if (/iPad|Tablet/i.test(ua)) {
    deviceType = 'tablet';
  }
  
  return {
    userAgent: ua,
    browserName,
    browserVersion,
    osName,
    osVersion,
    deviceType,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
  };
}

/**
 * Fetch location info from IP geolocation service
 */
async function fetchLocationInfo(): Promise<LocationInfo> {
  try {
    // Using ipapi.co for free geolocation (1000 requests/day free tier)
    const response = await fetch('https://ipapi.co/json/', {
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });
    
    if (!response.ok) {
      return { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone };
    }
    
    const data = await response.json();
    
    return {
      ipAddress: data.ip,
      city: data.city,
      region: data.region,
      country: data.country_name,
      countryCode: data.country_code,
      timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      latitude: data.latitude,
      longitude: data.longitude,
    };
  } catch (error) {
    console.warn('Failed to fetch location info:', error);
    return {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }
}

/**
 * Check if error is a "table not found" error and disable analytics if so
 */
function checkTableNotFoundError(error: any): boolean {
  // Check for RLS policy violations (42501) - these are expected if RLS is enabled
  if (error?.code === '42501') {
    // RLS policy violation - this is expected and can be ignored
    return true;
  }
  if (error?.code === 'PGRST205' || error?.message?.includes('Could not find the table')) {
    if (analyticsEnabled) {
      console.warn('[Analytics] Tables not found. Run migration 039_analytics_tracking.sql to enable analytics.');
      analyticsEnabled = false;
    }
    return true;
  }
  return false;
}

/**
 * Analytics Service
 */
export const analyticsService = {
  /**
   * Check if analytics is enabled
   */
  isEnabled(): boolean {
    return analyticsEnabled;
  },

  /**
   * Start a new user session (call on login)
   */
  async startSession(
    userId: string,
    authProvider: string = 'email',
    loginMethod: string = 'password'
  ): Promise<SessionInfo | null> {
    if (!analyticsEnabled) return null;

    try {
      // Only track with a live Supabase session: user_sessions RLS requires
      // auth.uid() = user_id, so inserting with a stale/mismatched id (e.g. a
      // Microsoft-auth user without a Supabase session) returns 403. Use the
      // authenticated id so the row always passes the check, or skip.
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return null;
      userId = authUser.id;

      // End any existing active session
      if (currentSession?.isActive) {
        await this.endSession();
      }
      
      // Get device and location info
      const deviceInfo = parseUserAgent();
      const locationInfo = await fetchLocationInfo();
      
      const { data, error } = await supabase
        .from('user_sessions')
        .insert({
          user_id: userId,
          session_start: new Date().toISOString(),
          is_active: true,
          user_agent: deviceInfo.userAgent,
          browser_name: deviceInfo.browserName,
          browser_version: deviceInfo.browserVersion,
          os_name: deviceInfo.osName,
          os_version: deviceInfo.osVersion,
          device_type: deviceInfo.deviceType,
          screen_width: deviceInfo.screenWidth,
          screen_height: deviceInfo.screenHeight,
          ip_address: locationInfo.ipAddress,
          city: locationInfo.city,
          region: locationInfo.region,
          country: locationInfo.country,
          country_code: locationInfo.countryCode,
          timezone: locationInfo.timezone,
          latitude: locationInfo.latitude,
          longitude: locationInfo.longitude,
          auth_provider: authProvider,
          login_method: loginMethod,
        })
        .select()
        .single();
      
      if (error) {
        if (!checkTableNotFoundError(error)) {
          console.error('Failed to start session:', error);
        }
        return null;
      }
      
      currentSession = {
        id: data.id,
        userId: data.user_id,
        sessionStart: new Date(data.session_start),
        isActive: true,
      };
      
      // Log the login activity
      await this.logActivity({
        activityType: 'login',
        activityCategory: 'auth',
        description: `User logged in via ${authProvider}`,
        details: {
          authProvider,
          loginMethod,
          browser: deviceInfo.browserName,
          os: deviceInfo.osName,
          device: deviceInfo.deviceType,
          location: locationInfo.city ? `${locationInfo.city}, ${locationInfo.country}` : undefined,
        },
      });
      
      return currentSession;
    } catch (error: any) {
      checkTableNotFoundError(error);
      return null;
    }
  },
  
  /**
   * End the current session (call on logout or window close)
   */
  async endSession(): Promise<void> {
    if (!currentSession) return;
    
    try {
      // End current page view
      await this.endPageView();
      
      // Update session end time
      await supabase.rpc('end_user_session', {
        p_session_id: currentSession.id,
      });
      
      // Log logout activity
      await this.logActivity({
        activityType: 'logout',
        activityCategory: 'auth',
        description: 'User logged out',
      });
      
      currentSession = null;
    } catch (error) {
      console.error('Error ending session:', error);
    }
  },
  
  /**
   * Get current session
   */
  getCurrentSession(): SessionInfo | null {
    return currentSession;
  },
  
  /**
   * Start tracking a page view
   */
  async startPageView(event: PageViewEvent): Promise<void> {
    if (!analyticsEnabled) return;
    
    try {
      // End previous page view
      await this.endPageView();
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      pageEntryTime = new Date();
      
      const { data, error } = await supabase
        .from('page_views')
        .insert({
          user_id: user.id,
          session_id: currentSession?.id,
          page_path: redactPublicTokenPath(event.pagePath),
          page_title: event.pageTitle || document.title,
          page_category: event.pageCategory,
          referrer_path: redactPublicTokenPath(event.referrerPath),
          entry_timestamp: pageEntryTime.toISOString(),
          query_params: event.queryParams || {},
        })
        .select('id')
        .single();
      
      if (error) {
        checkTableNotFoundError(error);
      } else if (data) {
        currentPageViewId = data.id;
      }
    } catch (error: any) {
      checkTableNotFoundError(error);
    }
  },
  
  /**
   * End current page view (calculate time on page)
   */
  async endPageView(maxScrollDepth?: number): Promise<void> {
    if (!analyticsEnabled || !currentPageViewId || !pageEntryTime) return;
    
    try {
      const timeOnPage = Math.round((Date.now() - pageEntryTime.getTime()) / 1000);
      
      const { error } = await supabase
        .from('page_views')
        .update({
          exit_timestamp: new Date().toISOString(),
          time_on_page_seconds: timeOnPage,
          max_scroll_depth: maxScrollDepth,
        })
        .eq('id', currentPageViewId);
      
      if (error) checkTableNotFoundError(error);
      
      currentPageViewId = null;
      pageEntryTime = null;
    } catch (error: any) {
      checkTableNotFoundError(error);
    }
  },
  
  /**
   * Log general user activity
   */
  async logActivity(event: ActivityLogEvent): Promise<void> {
    if (!analyticsEnabled) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { error } = await supabase.from('user_activity_log').insert({
        user_id: user.id,
        session_id: currentSession?.id,
        activity_type: event.activityType,
        activity_category: event.activityCategory,
        description: event.description,
        entity_type: event.entityType,
        entity_id: event.entityId,
        details: event.details || {},
      });
      
      if (error) checkTableNotFoundError(error);
    } catch (error: any) {
      checkTableNotFoundError(error);
    }
  },
  
  /**
   * Track errors
   */
  async trackError(event: ErrorEvent): Promise<void> {
    if (!analyticsEnabled) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase.from('error_tracking').insert({
        user_id: user?.id,
        session_id: currentSession?.id,
        error_type: event.errorType,
        error_message: event.errorMessage,
        error_stack: event.errorStack,
        page_path: redactPublicTokenPath(event.pagePath || window.location.pathname),
        component_name: event.componentName,
        action_attempted: event.actionAttempted,
        request_url: event.requestUrl,
        request_method: event.requestMethod,
        response_status: event.responseStatus,
        browser_info: parseUserAgent(),
      });
      
      if (error) checkTableNotFoundError(error);
    } catch (error: any) {
      checkTableNotFoundError(error);
    }
  },
};

// Set up window unload handler to end session
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    // end_user_session is SECURITY DEFINER + granted to anon, so it only needs
    // the project apikey — but navigator.sendBeacon can't set headers, so it
    // has always hit PostgREST with no apikey and 401'd. Use fetch(keepalive)
    // instead: it survives page unload like sendBeacon AND carries the apikey
    // header, so the session actually gets an end time. (RLS-flip safe: the
    // security-definer RPC bypasses user_sessions RLS.)
    if (currentSession) {
      fetch(`${supabaseUrl}/rest/v1/rpc/end_user_session`, {
        method: 'POST',
        keepalive: true,
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ p_session_id: currentSession.id }),
      }).catch(() => {
        /* best-effort on unload */
      });
    }
  });
  
  // Handle visibility change (tab switch/minimize)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && currentPageViewId) {
      analyticsService.endPageView();
    }
  });
}

export default analyticsService;
